import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as THREE from '@phdsoft/three';
import { GaugingElement } from '../platform/busines-data/SectionElements/InspectionElements/gaugingElement';
import { WorkableElement } from '../platform/busines-data/SectionElements/WorkableElement';
import { GraphicsMaterials } from '../digital-twin/graphics/graphics-materials';
import { Graphics } from '../digital-twin/graphics/graphics';
import { GraphicsRenderDecal } from '../digital-twin/graphics/graphics-render-decal';
import { AssetManager } from './asset-manager';
import { ELEMENTSTYPESIDS } from 'projects/phdsoft/common/src/lib/shared/constants/elementsTypesIds';
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model';
import { CollectionService } from '../collections/collection.service';
import { INSERTIONCONSTANTS, INSPECTIONS_TYPES_IDS } from '@phdsoft/common';
import { scopeForTarget } from './inspection-retarget.util';
import { InspectionPointInstanceManager } from '../digital-twin/graphics/inspection-point-instance-manager.service';

interface DraggedPointData {
  point: GaugingElement;
  originalPosition: THREE.Vector3;
  originalRotation: THREE.Euler;
  originalTarget: WorkableElement;
  originalScale: THREE.Vector3;
  originalScope: string;
  offset: THREE.Vector3;
}

@Injectable({ providedIn: 'root' })
export class AssetManagerInspectionDrag {

  private isDraggingPoint: boolean = false;
  private draggedPoint: GaugingElement | null = null;
  private originalPosition: THREE.Vector3 | null = null;
  private originalRotation: THREE.Euler | null = null;
  private originalTarget: WorkableElement | null = null;
  private originalScale: THREE.Vector3 | null = null;
  private originalScope: string | null = null;

  private draggedPoints: DraggedPointData[] = [];

  private hasMoved: boolean = false;

  private graphics: Graphics;
  private graphicsRenderDecal: GraphicsRenderDecal;
  private assetManager: AssetManager;
  private collectionService: CollectionService;
  private snackBar: MatSnackBar;
  private referenceDate: Date;
  private prepareInspectionBeforeSaving: (element: GaugingElement) => Promise<ElementResultInfo>;
  private instanceManager: InspectionPointInstanceManager;

  // --- PERF: Pooled objects to avoid per-move allocations ---
  private _raycaster = new THREE.Raycaster();
  private _mouse3D = new THREE.Vector3();
  private _mouse2D = new THREE.Vector2();
  private _screenPos = new THREE.Vector3();
  private _ndcVec = new THREE.Vector2();

  // --- PERF: rAF throttle state ---
  private _pendingDragEvent: any = null;
  private _dragRafId: number = 0;

  // --- PERF: Cached raycast targets (built once per drag session) ---
  private _cachedRaycastTargets: THREE.Mesh[] | null = null;

  // --- PERF: Element lookup map for O(1) validation ---
  private _elementLookup: Map<string, { element: WorkableElement; sectionId: string }> | null = null;

  // --- PERF: Cached orientation by intersected element ---
  private _lastIntersectedMeshName: string | null = null;
  private _lastOrientationResult: { rotation: THREE.Euler; normal: number[] | null } | null = null;

  public get isPointDragging(): boolean {
    return this.isDraggingPoint;
  }

  public get currentDraggedPoint(): GaugingElement | null {
    return this.draggedPoint;
  }

  public get hasMovedDuringDrag(): boolean {
    return this.hasMoved;
  }

  public initialize(
    graphics: Graphics,
    graphicsRenderDecal: GraphicsRenderDecal,
    assetManager: AssetManager,
    collectionService: CollectionService,
    snackBar: MatSnackBar,
    getReferenceDateFn: () => Date,
    prepareInspectionBeforeSaving: (element: GaugingElement) => Promise<ElementResultInfo>,
    instanceManager?: InspectionPointInstanceManager
  ): void {
    this.graphics = graphics;
    this.graphicsRenderDecal = graphicsRenderDecal;
    this.assetManager = assetManager;
    this.collectionService = collectionService;
    this.snackBar = snackBar;
    this.prepareInspectionBeforeSaving = prepareInspectionBeforeSaving;
    if (instanceManager) {
      this.instanceManager = instanceManager;
    }

    Object.defineProperty(this, 'referenceDate', {
      get: getReferenceDateFn,
      configurable: true
    });
  }

  public startInspectionPointDrag(inspectionPoint: GaugingElement): void {
    if (!inspectionPoint) return;

    this.isDraggingPoint = true;
    this.hasMoved = false;
    this.draggedPoint = inspectionPoint;
    this.originalPosition = inspectionPoint.SurfaceDesign.position.clone();
    this.originalRotation = inspectionPoint.SurfaceDesign.rotation.clone();
    this.originalTarget = inspectionPoint.targetElement;
    this.originalScale = inspectionPoint.SurfaceDesign.scale.clone();
    this.originalScope = inspectionPoint.elementResult?.Scope || null;

    this.assetManager.functionalityMode = 'INSPECTIONPOINTMOVE';

    this.applyDragMaterial(inspectionPoint);

    // PERF: Build caches at drag start (reused throughout drag session)
    this._cachedRaycastTargets = this._buildRaycastTargets();
    this._elementLookup = this._buildElementLookupMap();

    // PERF: Enable lightweight render mode during drag
    if (this.graphics) {
      this.graphics.isDragMode = true;
    }

    this.draggedPoints = [];
    const selectedInspections = this.assetManager.selectedInspections;

    if (selectedInspections && selectedInspections.length > 1) {
      const isPrimaryInSelection = selectedInspections.some(s => s.Guid === inspectionPoint.Guid);

      if (isPrimaryInSelection) {
        for (const selected of selectedInspections) {
          const selectedPoint = selected as GaugingElement;
          if (selectedPoint.Guid === inspectionPoint.Guid) continue;

          const offset = selectedPoint.SurfaceDesign.position.clone().sub(inspectionPoint.SurfaceDesign.position);

          this.draggedPoints.push({
            point: selectedPoint,
            originalPosition: selectedPoint.SurfaceDesign.position.clone(),
            originalRotation: selectedPoint.SurfaceDesign.rotation.clone(),
            originalTarget: selectedPoint.targetElement,
            originalScale: selectedPoint.SurfaceDesign.scale.clone(),
            originalScope: selectedPoint.elementResult?.Scope || '',
            offset: offset
          });

          this.applyDragMaterial(selectedPoint);
        }
        console.log(`Started dragging ${this.draggedPoints.length + 1} inspection points`);
      } else {
        console.log('Started dragging inspection point:', inspectionPoint.Guid);
      }
    } else {
      console.log('Started dragging inspection point:', inspectionPoint.Guid);
    }
  }

  private applyDragMaterial(point: GaugingElement): void {
    if (this.instanceManager) {
      // InstancedMesh path: set yellow drag color directly on the instance buffer.
      // No need to clone/swap material — the instance color is per-slot in the GPU buffer.
      this.instanceManager.setInstanceColorDirect(
        point,
        InspectionPointInstanceManager.DRAG_COLOR
      );
    } else if (point.SurfaceDesign.material) {
      // Legacy path for non-instanced elements (repairs, sensors, etc.)
      point.originalMaterial = point.SurfaceDesign.material;
      point.SurfaceDesign.material = GraphicsMaterials.pointMaterial.clone();
      (point.SurfaceDesign.material as THREE.MeshBasicMaterial).color.setHex(0xffff00);
    }
  }

  public updateInspectionPointPosition(ev: any): void {
    if (!this.isDraggingPoint || !this.draggedPoint) return;

    this.hasMoved = true;

    // PERF: Coalesce rapid mouse events to one processing per animation frame.
    // Store the latest event; only the most recent position matters.
    this._pendingDragEvent = ev;
    if (!this._dragRafId) {
      this._dragRafId = requestAnimationFrame(() => {
        this._dragRafId = 0;
        if (this._pendingDragEvent) {
          this._processInspectionPointPosition(this._pendingDragEvent);
          this._pendingDragEvent = null;
        }
      });
    }
  }

  private _processInspectionPointPosition(ev: any): void {
    if (!this.isDraggingPoint || !this.draggedPoint) return;

    // PERF: Reuse pooled Vector3/Vector2/Raycaster — zero allocations per move
    this._mouse3D.set(
      ((ev.clientX ? ev.clientX : ev.center.x) - this.graphics.offsetX) / window.innerWidth * 2 - 1,
      -(((ev.clientY ? ev.clientY : ev.center.y) - this.graphics.offsetY) / window.innerHeight) * 2 + 1,
      0.5
    );
    this._mouse2D.set(this._mouse3D.x, this._mouse3D.y);
    this._raycaster.setFromCamera(this._mouse2D, this.graphics.camera);

    // PERF: Use cached raycast targets (built once at drag start)
    const raycastTargets = this.collectRaycastTargets();
    const intersects = this._raycaster.intersectObjects(raycastTargets, false);

    // Only surfaces in the dragged point's own section are drop candidates. The
    // raycast targets deliberately include HIDDEN meshes from other sections (see
    // _buildRaycastTargets) which can sit between the camera and the visible surface —
    // snapping the point onto such a hit teleports it toward the camera, so it
    // appears to grow. Pick the nearest same-section hit; when there is none
    // (invalid position), fall through to the empty-space branch which tracks the
    // cursor at the point's current depth with red feedback.
    let intersection: any = null;
    for (const hit of intersects) {
      const hitLookup = this._elementLookup?.get((hit.object as any)?.name);
      if (hitLookup && hitLookup.sectionId === this.draggedPoint.SectionId) {
        intersection = hit;
        break;
      }
    }

    if (intersection) {
      this.updateDraggedPointWithProperOrientation(ev, intersection);

      // REND-06: sync primary point position to InstancedMesh slot via addUpdateRange
      // partial buffer upload (64 bytes = 16 float32s per instance per frame).
      // Do NOT call flushDirtyBuffers here — the render loop handles it before composer.render().
      if (this.instanceManager) {
        this.instanceManager.syncInstance(this.draggedPoint, true);
        this.instanceManager.syncProxyMatrix(this.draggedPoint);  // SEL-04: outline follows drag
      }

      // Validity preview uses the SAME validation as finishInspectionPointMove (section,
      // type and scope) so the green/red feedback matches what the drop will do.
      // The previous TypeId-only shortcut showed green over invalid positions.
      const isValidPreview = this.validateDropPositionForPoint(
        this.draggedPoint,
        this.originalTarget?.TypeId,
        this.originalScope
      ).valid;
      this.updateSinglePointVisualFeedback(this.draggedPoint, isValidPreview);

      if (this.draggedPoints.length > 0) {
        const primaryNewPosition = this.draggedPoint.SurfaceDesign.position;

        for (const dragData of this.draggedPoints) {
          // PERF: Offset-based positioning — no raycast per secondary point.
          // Use primary's surface position + stored offset, inherit primary's rotation.
          dragData.point.SurfaceDesign.position.copy(primaryNewPosition).add(dragData.offset);
          dragData.point.SurfaceDesign.rotation.copy(this.draggedPoint.SurfaceDesign.rotation);

          // REND-06: sync each secondary point — N addUpdateRange calls per move event
          // for N selected points. Ranges accumulate in THREE.js BufferAttribute.
          if (this.instanceManager) {
            this.instanceManager.syncInstance(dragData.point, true);
            this.instanceManager.syncProxyMatrix(dragData.point);  // SEL-04: outline follows drag
          }

          // Each secondary is validated against its own dropped position so points
          // on invalid spots turn red independently of the primary's status.
          const secondaryValid = this.validateDropPositionForPoint(
            dragData.point,
            dragData.originalTarget?.TypeId,
            dragData.originalScope
          ).valid;
          this.updateSinglePointVisualFeedback(dragData.point, secondaryValid);
        }
      }
    } else {
      // Dragging to empty space (no mesh target) — position point along ray
      // at the same depth as its current position for smooth cursor tracking.
      const depth = this.graphics.camera.position.distanceTo(
        this.draggedPoint.SurfaceDesign.position
      );
      this._raycaster.ray.at(depth, this._mouse3D);
      this.draggedPoint.SurfaceDesign.position.copy(this._mouse3D);

      this.updateSinglePointVisualFeedback(this.draggedPoint, false);

      if (this.instanceManager) {
        this.instanceManager.syncInstance(this.draggedPoint, true);
        this.instanceManager.syncProxyMatrix(this.draggedPoint);
      }

      if (this.draggedPoints.length > 0) {
        const primaryNewPosition = this.draggedPoint.SurfaceDesign.position;
        for (const dragData of this.draggedPoints) {
          dragData.point.SurfaceDesign.position.copy(primaryNewPosition).add(dragData.offset);
          dragData.point.SurfaceDesign.rotation.copy(this.draggedPoint.SurfaceDesign.rotation);

          if (this.instanceManager) {
            this.instanceManager.syncInstance(dragData.point, true);
            this.instanceManager.syncProxyMatrix(dragData.point);
          }

          const secondaryValid = this.validateDropPositionForPoint(
            dragData.point,
            dragData.originalTarget?.TypeId,
            dragData.originalScope
          ).valid;
          this.updateSinglePointVisualFeedback(dragData.point, secondaryValid);
        }
      }
    }
  }

  private validateDropPositionForPoint(point: GaugingElement, originalTypeId: ELEMENTSTYPESIDS | undefined, originalScope?: string): { valid: boolean, target: WorkableElement | null } {
    // validateDropPosition uses _screenPos internally (copy, not clone), so passing the live reference is safe
    return this.validateDropPosition(point.SurfaceDesign.position, originalTypeId, point.SectionId, originalScope);
  }

  private updateSinglePointVisualFeedback(point: GaugingElement, isValid: boolean): void {
    if (this.instanceManager) {
      // InstancedMesh path: set green (valid) or red (invalid) directly on the instance buffer.
      const feedbackColor = isValid
        ? InspectionPointInstanceManager.DRAG_VALID_COLOR
        : InspectionPointInstanceManager.DRAG_INVALID_COLOR;
      this.instanceManager.setInstanceColorDirect(point, feedbackColor);
    } else {
      // Legacy path for non-instanced elements (repairs, sensors, etc.)
      const color = isValid ? 0x00ff00 : 0xff0000;
      (point.SurfaceDesign.material as THREE.MeshBasicMaterial).color.setHex(color);
    }
  }

  private updateDraggedPointWithProperOrientation(ev: any, intersection: any): WorkableElement | null {
    const targetElement = this.findTargetElementAtIntersection(intersection);

    if (!targetElement) {
      this.draggedPoint.SurfaceDesign.position.copy(intersection.point);
      return null;
    }

    const meshName = intersection.object?.name;
    const elementTypeName = ELEMENTSTYPESIDS.getElementTypeName(targetElement.TypeId as string);

    // PERF: Cache orientation by intersected element — only recompute when crossing mesh boundaries.
    // For flat surfaces (Plate/Solid) the rotation is constant per mesh, so cache is always valid.
    // For curved elements (Cable/Pipe) the normal varies, so skip the cache.
    const isCurved = elementTypeName === 'Cable' || elementTypeName === 'Pipe';
    if (!isCurved && meshName && meshName === this._lastIntersectedMeshName && this._lastOrientationResult) {
      this.draggedPoint.SurfaceDesign.position.copy(intersection.point);
      this.draggedPoint.SurfaceDesign.rotation.copy(this._lastOrientationResult.rotation);
      if (this._lastOrientationResult.normal && this.draggedPoint.elementResult?.ElementInfo) {
        this.draggedPoint.elementResult.ElementInfo.normal = [...this._lastOrientationResult.normal];
      }
      return targetElement;
    }

    const sectionId = this.draggedPoint.SectionId;

    let inspectionPointData: any = null;
    let pointNormal: any = { normal: null };

    try {
      switch (elementTypeName) {
        case 'Plate':
        case 'Solid':
          const box = this.assetManager.asset.GetStructureVisibleRegions()[0].sections.find(x => x.Guid === sectionId)?.box3;
          if (box) {
            inspectionPointData = this.graphicsRenderDecal.insertPlatePoint(
              ev,
              box,
              this.assetManager.asset,
              sectionId,
              pointNormal
            );
          }
          break;
        case 'Stiffener':
          inspectionPointData = this.graphicsRenderDecal.insertStiffenerPoint(
            ev,
            this.assetManager.asset,
            sectionId
          );
          break;
        case 'FacePlate':
          inspectionPointData = this.graphicsRenderDecal.insertFacePlatePoint(
            ev,
            this.assetManager.asset,
            sectionId
          );
          break;
        case 'Bracket':
          inspectionPointData = this.graphicsRenderDecal.insertBracketPoint(
            ev,
            this.assetManager.asset,
            sectionId
          );
          break;
        case 'BeamPart':
          const beamBox = this.assetManager.asset.GetStructureVisibleRegions()[0].sections.find(x => x.Guid === sectionId)?.box3;
          if (beamBox) {
            inspectionPointData = this.graphicsRenderDecal.insertBeamPartPoint(
              ev,
              beamBox,
              this.assetManager.asset,
              sectionId
            );
          }
          break;
        case 'Cable':
          const cableBox = this.assetManager.asset.GetStructureVisibleRegions()[0].sections.find(x => x.Guid === sectionId)?.box3;
          if (cableBox) {
            inspectionPointData = this.graphicsRenderDecal.insertCablePoint(
              ev,
              cableBox,
              this.assetManager.asset,
              sectionId
            );
          }
          break;
        case 'Pipe':
          const pipeBox = this.assetManager.asset.GetStructureVisibleRegions()[0].sections.find(x => x.Guid === sectionId)?.box3;
          if (pipeBox) {
            inspectionPointData = this.graphicsRenderDecal.insertPipePoint(
              ev,
              pipeBox,
              this.assetManager.asset,
              sectionId
            );
          }
          break;
        default:
          this.draggedPoint.SurfaceDesign.position.copy(intersection.point);
          return targetElement;
      }
    } catch (e) {
      console.warn('[DRAG] Orientation calculation failed, using raw intersection point:', e);
    }

    if (inspectionPointData && inspectionPointData.length > 0) {
      const pointData = inspectionPointData[0];

      this.draggedPoint.SurfaceDesign.position.copy(pointData.point.position);

      if (pointData.point.rotation) {
        this.draggedPoint.SurfaceDesign.rotation.copy(pointData.point.rotation);
      }

      // Do NOT copy scale during drag — the point must keep its original size.
      // Scale is only relevant on final placement (handled by finishInspectionPointMove).

      if (pointNormal.normal) {
        this.draggedPoint.elementResult.ElementInfo.normal = [
          pointNormal.normal.x,
          pointNormal.normal.y,
          pointNormal.normal.z
        ];
      }

      // PERF: Cache orientation result for reuse when cursor stays on the same mesh
      this._lastIntersectedMeshName = meshName;
      this._lastOrientationResult = {
        rotation: this.draggedPoint.SurfaceDesign.rotation.clone(),
        normal: pointNormal.normal ? [pointNormal.normal.x, pointNormal.normal.y, pointNormal.normal.z] : null
      };
    } else {
      this.draggedPoint.SurfaceDesign.position.copy(intersection.point);
    }

    return targetElement;
  }

  /**
   * Returns cached raycast targets during drag, or builds fresh list.
   * PERF: During drag, targets are cached at drag start and reused throughout.
   */
  private collectRaycastTargets(): THREE.Mesh[] {
    if (this._cachedRaycastTargets) return this._cachedRaycastTargets;
    return this._buildRaycastTargets();
  }

  /**
   * Collect all meshes for drag raycasting, excluding InstancedMesh.
   * Uses GetVisibleGroups() (same source as DecalSelectionService) to include meshes
   * from all campaigns (structure, repair, sensor, etc.). Manually traverses each group
   * to bypass parent-group visibility checks that cause THREE.Raycaster to skip meshes
   * when intermediate groups like surfaceGroup have visible=false.
   */
  private _buildRaycastTargets(): THREE.Mesh[] {
    const meshes: THREE.Mesh[] = [];
    const groups = this.assetManager.asset.GetVisibleGroups();
    for (const group of groups) {
      group.traverse((obj: any) => {
        if (obj instanceof THREE.InstancedMesh) return;
        if (obj instanceof THREE.Mesh) meshes.push(obj);
      });
    }
    return meshes;
  }

  /**
   * PERF: O(1) element lookup via pre-built Map, replacing O(N³) nested loops.
   */
  private findTargetElementAtIntersection(intersection: any): WorkableElement | null {
    const meshName = intersection.object?.name;
    if (!meshName) return null;
    return this._elementLookup?.get(meshName)?.element ?? null;
  }

  /**
   * Build a lookup map from mesh name (element Guid) to WorkableElement + sectionId.
   * Built once per drag session for O(1) lookups.
   */
  private _buildElementLookupMap(): Map<string, { element: WorkableElement; sectionId: string }> {
    const map = new Map<string, { element: WorkableElement; sectionId: string }>();

    for (const campaign of this.assetManager.asset.StructureCampaigns) {
      for (const region of campaign.regionsWorkableElements) {
        for (const section of region.sections) {
          for (const el of section.surfaceElements) {
            map.set(el.Guid, { element: el, sectionId: section.Guid });
          }
          if (section.linearElements) {
            for (const el of section.linearElements) {
              map.set(el.Guid, { element: el, sectionId: section.Guid });
            }
          }
        }
      }
    }

    for (const campaign of this.assetManager.asset.RepairCampaigns) {
      for (const region of campaign.regionsWorkableElements) {
        for (const section of region.sections) {
          for (const el of section.surfaceElements) {
            map.set(el.Guid, { element: el, sectionId: section.Guid });
          }
        }
      }
    }

    return map;
  }

  private validateDropPosition(position: THREE.Vector3, originalTypeId: ELEMENTSTYPESIDS | undefined, sectionId: string, originalScope?: string): { valid: boolean, target: WorkableElement | null } {
    // PERF: Reuse pooled objects for screen projection + raycasting
    this._screenPos.copy(position).project(this.graphics.camera);
    this._ndcVec.set(this._screenPos.x, this._screenPos.y);
    this._raycaster.setFromCamera(this._ndcVec, this.graphics.camera);

    const raycastTargets = this.collectRaycastTargets();
    const intersects = this._raycaster.intersectObjects(raycastTargets, false);

    if (intersects.length === 0) {
      return { valid: false, target: null };
    }

    // The validation ray runs from the camera through the dropped position, but the
    // raycast targets deliberately include meshes under invisible groups (see
    // _buildRaycastTargets). In SECTIONS mode every other section is hidden yet still
    // raycastable, so intersects[0] (nearest to the CAMERA) is frequently a hidden
    // occluder from another section — which rejected every drop. The dropped point
    // sits ON its intended surface, so instead resolve the hit nearest to the dropped
    // position that belongs to the point's own section, and reject drops that are not
    // actually on a surface (e.g. released over empty space).
    const MAX_SURFACE_DISTANCE_SQ = 0.05 * 0.05; // meters², placement puts points directly on surfaces
    let targetElement: WorkableElement | null = null;
    let bestDistSq = MAX_SURFACE_DISTANCE_SQ;
    for (const hit of intersects) {
      const distSq = hit.point.distanceToSquared(position);
      if (distSq >= bestDistSq) continue;
      const lookup = this._elementLookup?.get((hit.object as any)?.name);
      if (lookup && lookup.sectionId === sectionId) {
        bestDistSq = distSq;
        targetElement = lookup.element;
      }
    }

    if (targetElement && targetElement.TypeId === originalTypeId) {
      if (originalScope) {
        const newScope = this.getScopeForElement(targetElement);
        if (newScope !== originalScope) {
          return { valid: false, target: null };
        }
      }
      return { valid: true, target: targetElement };
    }

    return { valid: false, target: null };
  }

  private getScopeForElement(element: WorkableElement): string {
    // single implementation lives in inspection-retarget.util; unresolved/unknown -> 'NONE' as before
    return scopeForTarget(element) ?? 'NONE';
  }

  private animatePointReturn(inspectionPoint: GaugingElement, targetPosition: THREE.Vector3, targetRotation?: THREE.Euler, targetScale?: THREE.Vector3): void {
    const startPosition = inspectionPoint.SurfaceDesign.position.clone();
    const startRotation = inspectionPoint.SurfaceDesign.rotation.clone();
    const startScale = inspectionPoint.SurfaceDesign.scale.clone();
    const duration = 500;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easedProgress = 1 - Math.pow(1 - progress, 3);

      inspectionPoint.SurfaceDesign.position.lerpVectors(startPosition, targetPosition, easedProgress);

      if (targetRotation) {
        inspectionPoint.SurfaceDesign.rotation.x = startRotation.x + (targetRotation.x - startRotation.x) * easedProgress;
        inspectionPoint.SurfaceDesign.rotation.y = startRotation.y + (targetRotation.y - startRotation.y) * easedProgress;
        inspectionPoint.SurfaceDesign.rotation.z = startRotation.z + (targetRotation.z - startRotation.z) * easedProgress;
      }

      if (targetScale) {
        inspectionPoint.SurfaceDesign.scale.lerpVectors(startScale, targetScale, easedProgress);
      }

      // REND-06: sync the animated position back to the InstancedMesh slot on each frame.
      // Uses drag=false (normal deferred flush path) since this is a snap-back, not a
      // streaming drag move. The render loop will flush on the next animation frame.
      if (this.instanceManager) {
        this.instanceManager.syncInstance(inspectionPoint, false);
        this.instanceManager.syncProxyMatrix(inspectionPoint);  // SEL-04: outline follows snap-back
      }
      this.graphics.requestRender();

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        inspectionPoint.SurfaceDesign.position.copy(targetPosition);
        if (targetRotation) {
          inspectionPoint.SurfaceDesign.rotation.copy(targetRotation);
        }
        if (targetScale) {
          inspectionPoint.SurfaceDesign.scale.copy(targetScale);
        }

        // Commit the final restored position to the InstancedMesh slot.
        if (this.instanceManager) {
          this.instanceManager.syncInstance(inspectionPoint, false);
          this.instanceManager.syncProxyMatrix(inspectionPoint);  // SEL-04: final outline position
          // Restore original corrosion color via material.name path after snap-back.
          this.instanceManager.setInstanceColor(inspectionPoint, inspectionPoint.SurfaceDesign.material as THREE.Material);
        }
        this.graphics.requestRender();

        this.flashPoint(inspectionPoint);
      }
    };

    animate();
  }

  private flashPoint(inspectionPoint: GaugingElement): void {
    const flashColor = new THREE.Color(0xffffff);
    let flashCount = 0;
    const maxFlashes = 2;

    if (this.instanceManager) {
      // InstancedMesh path: flash between white and the original corrosion color.
      // Capture the original material reference once before the flash loop starts.
      const originalMaterial = inspectionPoint.SurfaceDesign.material as THREE.Material;

      const flash = () => {
        flashCount++;
        const isWhite = flashCount % 2 === 1;

        if (isWhite) {
          this.instanceManager.setInstanceColorDirect(inspectionPoint, flashColor);
        } else {
          this.instanceManager.setInstanceColor(inspectionPoint, originalMaterial);
        }
        this.graphics.requestRender();

        if (flashCount < maxFlashes * 2) {
          setTimeout(flash, 100);
        }
      };

      flash();
    } else {
      // Legacy path for non-instanced elements (repairs, sensors, etc.)
      const originalColor = (inspectionPoint.SurfaceDesign.material as THREE.MeshBasicMaterial).color.clone();

      const flash = () => {
        flashCount++;
        const isWhite = flashCount % 2 === 1;

        (inspectionPoint.SurfaceDesign.material as THREE.MeshBasicMaterial).color.copy(isWhite ? flashColor : originalColor);
        this.graphics.requestRender();

        if (flashCount < maxFlashes * 2) {
          setTimeout(flash, 100);
        }
      };

      flash();
    }
  }

  public async finishInspectionPointMove(): Promise<void> {
    if (!this.isDraggingPoint || !this.draggedPoint) return;

    if (!this.hasMoved) {
      this.cancelInspectionPointDrag();
      return;
    }

    const isMultipleMove = this.draggedPoints.length > 0;

    interface ValidatedPoint {
      point: GaugingElement;
      position: THREE.Vector3;
      target?: WorkableElement;
      isValid: boolean;
    }

    const validatedPoints: ValidatedPoint[] = [];
    let validCount = 0;
    let invalidCount = 0;

    const currentPosition = this.draggedPoint.SurfaceDesign.position.clone();
    const primaryValidation = this.validateDropPosition(currentPosition, this.originalTarget?.TypeId, this.draggedPoint.SectionId, this.originalScope);

    if (primaryValidation.valid && primaryValidation.target) {
      validatedPoints.push({
        point: this.draggedPoint,
        position: currentPosition,
        target: primaryValidation.target,
        isValid: true
      });
      // Commit final dropped position to InstancedMesh slot via normal deferred flush.
      // drag=false — one-time update, not streaming; _savedMatrix updated for safe hide/show restore.
      if (this.instanceManager) {
        this.instanceManager.syncInstance(this.draggedPoint, false);
        this.instanceManager.syncProxyMatrix(this.draggedPoint);  // SEL-04: final outline position
      }
      validCount++;
    } else {
      this.animatePointReturn(this.draggedPoint, this.originalPosition, this.originalRotation, this.originalScale);
      invalidCount++;
    }

    if (isMultipleMove) {
      for (const dragData of this.draggedPoints) {
        const secondaryPosition = dragData.point.SurfaceDesign.position.clone();
        const secondaryValidation = this.validateDropPositionForPoint(dragData.point, dragData.originalTarget?.TypeId, dragData.originalScope);

        if (secondaryValidation.valid) {
          validatedPoints.push({
            point: dragData.point,
            position: secondaryPosition,
            target: secondaryValidation.target,
            isValid: true
          });
          // Commit final dropped position for each valid secondary point.
          if (this.instanceManager) {
            this.instanceManager.syncInstance(dragData.point, false);
            this.instanceManager.syncProxyMatrix(dragData.point);  // SEL-04: final outline position
          }
          validCount++;
        } else {
          this.animatePointReturn(dragData.point, dragData.originalPosition, dragData.originalRotation, dragData.originalScale);
          invalidCount++;
        }
      }
    }

    this.restoreDraggedPointsMaterials();

    this.resetDragState();

    this.assetManager.enableMultipleSelection();

    console.log(`Finished dragging: ${validCount} valid, ${invalidCount} invalid`);

    if (validCount > 0) {
      this.snackBar.open(`Saving ${validCount} inspection point(s)...`, '', { duration: 2000 });

      this.saveValidatedPointsInBackground(validatedPoints).then((savedCount) => {
        if (savedCount === validCount) {
          this.snackBar.open(`${savedCount} inspection point(s) moved successfully`, '', { duration: 2000 });
        } else {
          this.snackBar.open(`${savedCount}/${validCount} point(s) saved`, '', { duration: 3000 });
        }
      }).catch((error) => {
        console.error('Error saving inspection points:', error);
        this.snackBar.open('Error saving some inspection points', '', { duration: 3000 });
      });
    } else if (invalidCount > 0) {
      this.snackBar.open('Invalid positions - all points returned to origin', '', { duration: 3000 });
    }
  }

  private async saveValidatedPointsInBackground(validatedPoints: Array<{
    point: GaugingElement;
    position: THREE.Vector3;
    target?: WorkableElement;
    isValid: boolean;
  }>): Promise<number> {
    const validPoints = validatedPoints.filter(v => v.isValid);
    if (validPoints.length === 0) return 0;

    try {
      const elementsToSave: ElementResultInfo[] = [];

      for (const validated of validPoints) {
        if (validated.target) {
          this.updateInspectionPointTargetData(validated.point, validated.target, validated.position);
        } else {
          this.updateInspectionPointPositionDataSync(validated.point, validated.position);
        }

        const clonedResultElement = await this.prepareInspectionBeforeSaving(validated.point);
        elementsToSave.push(clonedResultElement);
      }

      const inspectionTypeName = this.assetManager.listInpectionType?.find(t =>
        t.id === validPoints[0].point.elementResult.InspectionTypeId
      )?.name;

      await this.collectionService.saveInspectionElement(
        elementsToSave,
        this.referenceDate,
        inspectionTypeName
      );

      this.updateStateForSavedElements(validPoints.map(v => v.point));

      return validPoints.length;
    } catch (error) {
      console.error('Error in bulk save of inspection points:', error);
      return 0;
    }
  }

  private updateInspectionPointPositionDataSync(inspectionElement: GaugingElement, newPosition: THREE.Vector3): void {
    inspectionElement.elementResult.ElementInfo.vertices = [newPosition.x, newPosition.y, newPosition.z];
  }

  private updateInspectionPointTargetData(inspectionElement: GaugingElement, newTarget: WorkableElement, newPosition: THREE.Vector3): void {
    inspectionElement.elementResult.TargetGeometryId = newTarget.Guid;
    inspectionElement.elementResult.TargetGeometryIds = [newTarget.Guid];
    inspectionElement.elementResult.ElementInfo.vertices = [newPosition.x, newPosition.y, newPosition.z];
    // A drag never moves the mesh between regions, but the drop target may come from another
    // region that shares this section Guid (overlapping regions). In-memory lookups are keyed on
    // regionId, so keep the inspection's regionId/regionName; regionIds keeps its previous
    // assignment from the target (unguarded, matching elementResult.RegionIds below).
    const { regionId, regionName } = inspectionElement;
    inspectionElement.retarget(newTarget);
    inspectionElement.regionId = regionId;
    inspectionElement.regionName = regionName;
    inspectionElement.regionIds = newTarget.regionIds;

    inspectionElement.elementResult.RegionIds = newTarget.regionIds;
    inspectionElement.elementResult.InsertedRegionId = newTarget.regionId;
  }

  private updateStateForSavedElements(inspectionElements: GaugingElement[]): void {
    for (const inspectionElement of inspectionElements) {
      this.updateStateForSavedElement(inspectionElement);
    }
  }

  private updateStateForSavedElement(inspectionElement: GaugingElement): void {
    this.removeHighlightFromElement(inspectionElement);

    this.markElementAsSaved(inspectionElement);

    this.handleDegradationOrColorUpdateForElement(inspectionElement);

    this.cleanupTemporarySelectionsForElement(inspectionElement);
  }

  private removeHighlightFromElement(inspectionElement: GaugingElement): void {
    if (inspectionElement.isHighlightedByFilter) {
      inspectionElement.SurfaceDesign.material = GraphicsMaterials.selectedBasicElementMaterial;
    }
  }

  private markElementAsSaved(inspectionElement: GaugingElement): void {
    inspectionElement.elementResult.Element.saved = true;
  }

  private handleDegradationOrColorUpdateForElement(inspectionElement: GaugingElement): void {
    try {
      const isThicknessGauging = inspectionElement.elementResult.InspectionTypeId === INSPECTIONS_TYPES_IDS.THICKNESS_GAUGING;
      const shouldEstimateDegradationCurve = this.assetManager.shouldEstimateDegradationCurve(inspectionElement);

      if (this.assetManager.degradationMode && isThicknessGauging) {
        if (shouldEstimateDegradationCurve) {
          this.assetManager.estimateElementDegradationCurve(
            inspectionElement.Guid,
            inspectionElement.targetElementId,
            false
          );
        } else {
          this.assetManager.removeInspectionDegradationCurveParameters(inspectionElement.Guid);
          this.assetManager.estimateDegradationCurvesByTarget(
            inspectionElement.targetElementId,
            inspectionElement.regionIds,
            inspectionElement.SectionId
          );
        }
      } else {
        this.assetManager.updateInspectionColorById(inspectionElement.Guid);
      }
    } catch (error) {
      console.error('Error updating degradation or color for element:', inspectionElement.Guid, error);
    }
  }

  private cleanupTemporarySelectionsForElement(inspectionElement: GaugingElement): void {
    this.assetManager.deletePointFromTempArray(inspectionElement.elementResult.Element);
  }

  private restoreDraggedPointsMaterials(): void {
    if (this.draggedPoint) {
      if (this.instanceManager) {
        // InstancedMesh path: restore original corrosion color via material.name -> _COLOR_MAP lookup.
        // SurfaceDesign.material still holds the phantom material reference whose .name encodes
        // the corrosion state (e.g. 'PointMaterialRed', 'PointMaterialGreen').
        this.instanceManager.setInstanceColor(this.draggedPoint, this.draggedPoint.SurfaceDesign.material as THREE.Material);
      } else if (this.draggedPoint.originalMaterial) {
        // Legacy path for non-instanced elements (repairs, sensors, etc.)
        this.draggedPoint.SurfaceDesign.material = this.draggedPoint.originalMaterial;
      }
    }

    for (const dragData of this.draggedPoints) {
      if (this.instanceManager) {
        this.instanceManager.setInstanceColor(dragData.point, dragData.point.SurfaceDesign.material as THREE.Material);
      } else if (dragData.point.originalMaterial) {
        dragData.point.SurfaceDesign.material = dragData.point.originalMaterial;
      }
    }
  }

  private resetDragState(): void {
    this.isDraggingPoint = false;
    this.draggedPoint = null;
    this.originalPosition = null;
    this.originalRotation = null;
    this.originalTarget = null;
    this.originalScale = null;
    this.originalScope = null;
    this.draggedPoints = [];
    this.hasMoved = false;

    // PERF: Cancel pending rAF and clear caches
    if (this._dragRafId) {
      cancelAnimationFrame(this._dragRafId);
      this._dragRafId = 0;
    }
    this._pendingDragEvent = null;
    this._cachedRaycastTargets = null;
    this._elementLookup = null;
    this._lastIntersectedMeshName = null;
    this._lastOrientationResult = null;

    // PERF: Restore normal render mode
    if (this.graphics) {
      this.graphics.isDragMode = false;
    }
  }

  public cancelInspectionPointDrag(): void {
    if (!this.isDraggingPoint || !this.draggedPoint) return;

    if (this.originalPosition) {
      this.draggedPoint.SurfaceDesign.position.copy(this.originalPosition);
    }
    if (this.originalRotation) {
      this.draggedPoint.SurfaceDesign.rotation.copy(this.originalRotation);
    }
    if (this.originalScale) {
      this.draggedPoint.SurfaceDesign.scale.copy(this.originalScale);
    }

    // Sync restored position to InstancedMesh slot after phantom reset.
    // drag=false — one-time restore, not streaming; updates _savedMatrix for safe hide/show.
    if (this.instanceManager) {
      this.instanceManager.syncInstance(this.draggedPoint, false);
      this.instanceManager.syncProxyMatrix(this.draggedPoint);  // SEL-04: outline follows cancel restore
    }

    for (const dragData of this.draggedPoints) {
      dragData.point.SurfaceDesign.position.copy(dragData.originalPosition);
      dragData.point.SurfaceDesign.rotation.copy(dragData.originalRotation);
      dragData.point.SurfaceDesign.scale.copy(dragData.originalScale);

      // Sync each secondary point's restored position to the InstancedMesh slot.
      if (this.instanceManager) {
        this.instanceManager.syncInstance(dragData.point, false);
        this.instanceManager.syncProxyMatrix(dragData.point);  // SEL-04: outline follows cancel restore
      }
    }

    this.restoreDraggedPointsMaterials();

    this.resetDragState();

    this.assetManager.functionalityMode = 'MULTIPLESELECTION';

    console.log('Cancelled dragging inspection point(s)');
  }

}
